import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMIN › DOKUMENTUMOK › GENERÁLÁS › TÖRZSLAP-HATÁRIDŐ (backend/torzslap.js kimutatas)
 *
 *  20/2012. (VIII. 31.) EMMI rendelet 99. § (1): „Az iskola a tanulókról – a tanévkezdést követő
 *  harminc napon belül – egyéni törzslapot állít ki.” Tanulónként: van-e már tárolt (aláírt,
 *  időbélyegzett) törzslap, mikor állították ki, mennyi idő van hátra, ill. mennyit késik. A tanév
 *  közben felvett tanulónál a határidő a felvételtől számított 30 nap. Osztályonként egy gombbal
 *  kiállítható a hiányzóknak (POST /api/torzslap, csak a hiányzó tanulókra).
 */

type Allapot = 'kiallitva' | 'kesve' | 'hatra' | 'lejart' | 'kilepett';
interface Tanulo {
  oktazon: string; nev: string; osztaly_id: string; osztaly: string;
  jogviszony_kezdete: string | null; jogviszony_vege: string | null;
  hatarido: string; hatarido_alapja: 'tanevkezdes' | 'felvetel';
  allapot: Allapot; napok: number | null;
  kiallitva: string | null; utolso: string | null; verzio: number;
  iktatoszam: string | null; path: string | null;
}
interface Kimutatas {
  ev: number; tanev: string; tanevkezdes: string; alap_hatarido: string; ma: string; jogalap: string;
  hatarido_nap: number; elotte_nap: number;
  darab: { osszes: number; kiallitva: number; kesve: number; hatra: number; lejart: number; kilepett: number; hatra_7_napon_belul: number };
  tanulok: Tanulo[];
}

interface SablonEllenorzes {
  jogalap: string; kovetelmenyek: number; megvan: boolean; hely: string; olvashato: boolean;
  hianyok: Array<{ pont: string; nev: string; kulcsok: string[] }>;
  nincs_adat: Array<{ pont: string; nev: string }>;
}

@Component({
  selector: 'app-torzslap-hatarido',
  templateUrl: './torzslap-hatarido.component.html',
  styleUrls: ['./torzslap-hatarido.component.scss'],
})
export class TorzslapHataridoComponent implements OnChanges {
  @Input() intezmeny = '';
  @Input() ev: string | null = null;
  /** A generálás beállításai a szülő űrlapjáról (dátum, formátum, keresztnév). */
  @Input() datum = '';
  @Input() datumFormat = '';
  @Input() csakElsoKeresztnev = false;

  adat: Kimutatas | null = null;
  betolt = false;
  /** A törzslap-sablon 99. § (3) szerinti ellenőrzése (backend/torzslap.js sablonHianyok). */
  sablon: SablonEllenorzes | null = null;
  szuro: 'hianyzik' | 'mind' = 'hianyzik';
  fut: Record<string, boolean> = {};

  constructor(private http: HttpClient, private data: DataService) {}

  ngOnChanges(v: SimpleChanges) {
    if ((v['intezmeny'] || v['ev']) && this.intezmeny && this.ev) this.betoltes();
    if (v['intezmeny'] && this.intezmeny) this.sablonEllenorzes();
  }

  /**
   *  A feltöltött törzslap-sablonból hiányzó kötelező tartalmi elemek (20/2012. EMMI r. 99. § (3)).
   *  Csak jelzés: a generálás a régi sablonnal is megy; a hibája nem akadály, ezért csendes.
   */
  async sablonEllenorzes() {
    try {
      this.sablon = await firstValueFrom(this.http.get<SablonEllenorzes>(`/api/torzslap-ellenorzes/sablon/${this.intezmeny}`));
    } catch (_) {
      this.sablon = null;
    }
  }

  hianyFelsorolas(l: Array<{ pont: string; nev: string }> | undefined): string {
    return (l || []).map((h) => `${h.nev} (${h.pont})`).join(', ');
  }

  async betoltes() {
    if (!this.intezmeny || !this.ev) return;
    this.betolt = true;
    try {
      this.adat = await firstValueFrom(this.http.get<Kimutatas>(`/api/torzslap-hatarido/${this.intezmeny}?ev=${encodeURIComponent(this.ev)}`));
    } catch (e: any) {
      this.adat = null;
      this.data.sendToast(e?.error?.error || 'A törzslap-határidő kimutatás nem töltődött be.', 'error');
    } finally {
      this.betolt = false;
    }
  }

  /** Osztályonként csoportosítva, a szűrő szerint (a backend már állapot és osztály szerint rendez). */
  get csoportok(): { osztaly_id: string; osztaly: string; tanulok: Tanulo[]; hianyzo: Tanulo[] }[] {
    const m = new Map<string, { osztaly_id: string; osztaly: string; tanulok: Tanulo[]; hianyzo: Tanulo[] }>();
    for (const t of this.adat?.tanulok || []) {
      const hianyzik = t.allapot === 'hatra' || t.allapot === 'lejart';
      if (this.szuro === 'hianyzik' && !hianyzik) continue;
      if (!m.has(t.osztaly_id)) m.set(t.osztaly_id, { osztaly_id: t.osztaly_id, osztaly: t.osztaly, tanulok: [], hianyzo: [] });
      const cs = m.get(t.osztaly_id)!;
      cs.tanulok.push(t);
      if (hianyzik) cs.hianyzo.push(t);
    }
    return [...m.values()].sort((a, b) => String(a.osztaly).localeCompare(String(b.osztaly), 'hu', { numeric: true }));
  }

  pontos(d: string | null): string {
    if (!d) return '';
    return `${d.slice(0, 4)}.${d.slice(5, 7)}.${d.slice(8, 10)}.${d.length > 10 ? ' ' + d.slice(11, 16) : ''}`;
  }

  allapotSzoveg(t: Tanulo): string {
    switch (t.allapot) {
      case 'kiallitva': return 'Kiállítva';
      case 'kesve': return `Késve kiállítva (+${t.napok} nap)`;
      case 'hatra': return t.napok === 0 ? 'Ma jár le' : `Hátra: ${t.napok} nap`;
      case 'lejart': return `Késik: ${t.napok} napja`;
      case 'kilepett': return 'A határidő előtt kilépett';
      default: return '';
    }
  }

  szin(t: Tanulo): string {
    switch (t.allapot) {
      case 'kiallitva': return 'success';
      case 'kesve': return 'warning';
      case 'hatra': return (t.napok ?? 99) <= (this.adat?.elotte_nap ?? 7) ? 'warning' : 'medium';
      case 'lejart': return 'danger';
      default: return 'medium';
    }
  }

  async megnyit(t: Tanulo) {
    if (!t.path) return;
    await this.data.megnyitFajl(`/api/file/${t.path}`, `Törzslap – ${t.nev}`);
  }

  /** A hiányzó tanulók törzslapjának kiállítása (aláírva, időbélyeggel, tárolva) egy osztályban. */
  async kiallit(cs: { osztaly_id: string; osztaly: string; hianyzo: Tanulo[] }) {
    if (!cs.hianyzo.length || !this.ev) return;
    const p = new URLSearchParams({ osztaly: cs.osztaly_id, ev: this.ev });
    if (this.datum) p.set('datum', String(this.datum).slice(0, 10));
    if (this.datumFormat) p.set('datumFormat', this.datumFormat);
    if (this.csakElsoKeresztnev) p.set('csakElsoKeresztnev', '1');
    this.fut[cs.osztaly_id] = true;
    this.data.showLoading(`${cs.osztaly}: ${cs.hianyzo.length} törzslap aláírása és tárolása…`);
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/torzslap/${this.intezmeny}?${p.toString()}`,
        { oktazonok: cs.hianyzo.map((t) => t.oktazon) }));
      const hibaSzam = Array.isArray(r?.hibak) ? r.hibak.length : 0;
      if (hibaSzam) {
        const ok = String(r.hibak[0]?.hiba || '').slice(0, 200);
        this.data.sendToast(`${r?.generalt ?? 0} törzslap kiállítva, ${hibaSzam} sikertelen${ok ? ` (${ok})` : ''}.`, 'warning');
      } else {
        this.data.sendToast(`${r?.generalt ?? 0} törzslap aláírva, időbélyegezve és tárolva${r?.iktatgato ? ', iktatva' : ''}.`, 'success');
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törzslapok kiállítása nem sikerült.', 'error');
    } finally {
      this.fut[cs.osztaly_id] = false;
      this.data.hideLoading();
      this.betoltes();
    }
  }
}
